import { useState } from 'react';
import { Download, RotateCcw, History, Percent } from 'lucide-react';
import { asset, eur, num, SOURCE_FILE } from '../../lib/project';
import { MonthlySalesTable } from './MonthlySalesTable';

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const clampPct = (v, lo = -100, hi = 99.99) => Math.max(lo, Math.min(hi, Number(v) || 0));

export const PricingEditor = ({ plan, editProduct, editPlan, setPriceBasis, resetProduct }) => {
  const [basis, setBasis] = useState('discounted');
  const vat = Number(plan.vat) || 23;
  // net = price_gross / (1+vat) — note: price_gross is already the discounted price (I46:I51)
  const netOf = (p) => round2(p.price_gross / (1 + vat / 100));
  const grossFromNet = (net) => round2(net * (1 + vat / 100));
  const discountPctOf = (p) => Number(p.discount_percent) || 0;

  // List (table) price — undoes the discount already in price_gross to get O2:O7 price
  const listNetOf = (p) => {
    const net = netOf(p);
    const disc = discountPctOf(p);
    return disc > 0 && disc < 100 ? round2(net / (1 - disc / 100)) : net;
  };
  const listGrossOf = (p) => {
    const disc = discountPctOf(p);
    return disc > 0 && disc < 100 ? round2(p.price_gross / (1 - disc / 100)) : p.price_gross;
  };

  // Margin at current (discounted) price
  const marginEurOf = (p) => round2(netOf(p) - p.cost);
  const marginPctOf = (p) => {
    const n = netOf(p);
    return n > 0 ? round2((n - p.cost) / n * 100) : 0;
  };
  // Margin at list price (no discount)
  const listMarginEurOf = (p) => round2(listNetOf(p) - p.cost);
  const listMarginPctOf = (p) => {
    const n = listNetOf(p);
    return n > 0 ? round2((n - p.cost) / n * 100) : 0;
  };

  // Set target margin on the DISCOUNTED price → recalculates price_gross (with discount)
  const setMarginPct = (p, pct) => {
    const safe = clampPct(pct);
    const newNet = safe >= 100 ? 0 : p.cost / (1 - safe / 100);
    editProduct(p.id, 'price_gross', round2(newNet * (1 + vat / 100)));
  };
  // Set target margin on the LIST price → derives listNet then rebuilds price_gross with current discount
  const setListMarginPct = (p, pct) => {
    const safe = clampPct(pct);
    const newListNet = safe >= 100 ? 0 : p.cost / (1 - safe / 100);
    const disc = discountPctOf(p);
    const discountedNet = disc > 0 && disc < 100 ? newListNet * (1 - disc / 100) : newListNet;
    editProduct(p.id, 'price_gross', round2(discountedNet * (1 + vat / 100)));
  };

  // Current-row edits keep the existing behaviour
  const updateGross = (p, value) => editProduct(p.id, 'price_gross', round2(value));
  const updateNet = (p, value) => editProduct(p.id, 'price_gross', grossFromNet(value));
  const updateDiscount = (p, value) => editProduct(p.id, 'discount_percent', Math.max(0, Math.min(95, Number(value) || 0)));

  // List-row edits — reverse engineer price_gross from list value + current discount
  const updateListGross = (p, value) => {
    const disc = discountPctOf(p);
    const factor = disc > 0 && disc < 100 ? (1 - disc / 100) : 1;
    editProduct(p.id, 'price_gross', round2((Number(value) || 0) * factor));
  };
  const updateListNet = (p, value) => {
    const disc = discountPctOf(p);
    const factor = disc > 0 && disc < 100 ? (1 - disc / 100) : 1;
    const newDiscountedNet = (Number(value) || 0) * factor;
    editProduct(p.id, 'price_gross', round2(newDiscountedNet * (1 + vat / 100)));
  };

  return (
    <>
      <details className="pricing-source-details" data-testid="pricing-source-details">
        <summary data-testid="pricing-source-summary">Origem dos dados · folha de cálculo (opcional)</summary>
        <div className="pricing-source-banner" data-testid="pricing-source-banner">
          <div>
            <span>PREÇOS E CUSTOS · BASE PRINCIPAL</span>
            <strong>{SOURCE_FILE}</strong>
            <p>Fonte única partilhada com o Planeamento. Editar aqui reflete-se em ambas as vistas.</p>
          </div>
          <a data-testid="download-projection-base-link" href={asset('projection-xlsx', true)} target="_blank" rel="noopener noreferrer" aria-label="Descarregar Excel principal">
            <Download size={19} />
          </a>
        </div>

        <div className="pricing-basis-control">
          <label htmlFor="price-basis-select">Preços da folha
            <select id="price-basis-select" data-testid="price-basis-select" value={basis} onChange={(e) => setBasis(e.target.value)}>
              <option value="discounted">Com desconto calculado · I46:I51</option>
              <option value="list">Antes do desconto · O2:O7</option>
              <option value="commercial">Comercial arredondado · J46:J51</option>
            </select>
          </label>
          <button data-testid="apply-pricing-basis-button" onClick={() => setPriceBasis(basis)}>
            <RotateCcw size={13} />Repor todos os modelos
          </button>
          <p data-testid="active-pricing-basis">Base atual: {
            plan.price_basis === 'custom' ? 'valores personalizados'
              : plan.price_basis === 'list' ? 'antes do desconto'
              : plan.price_basis === 'commercial' ? 'comercial arredondado'
              : 'com desconto calculado'
          }. PVP com IVA e sem IVA sincronizados via {vat}% de IVA.</p>
        </div>
      </details>

      <div className="discount-toggle-bar" data-testid="discount-toggle-bar">
        <label className="discount-toggle-label">
          <input
            type="checkbox"
            checked={!!plan.apply_discount}
            onChange={(e) => editPlan('', 'apply_discount', e.target.checked)}
            data-testid="toggle-plan-apply-discount"
          />
          <div>
            <strong><Percent size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Simular com desconto por modelo</strong>
            <small>Aplica a coluna «Desconto (%)» abaixo ao PVP na receita, margem e indicadores. Toggle partilhado com o Planeamento.</small>
          </div>
        </label>
      </div>

      <div className="six-model-editor">
        {plan.products.map((p, i) => {
          const net = netOf(p);
          const m = marginEurOf(p);
          const pct = marginPctOf(p);
          const disc = discountPctOf(p);
          const lNet = listNetOf(p);
          const lGross = listGrossOf(p);
          const lM = listMarginEurOf(p);
          const lPct = listMarginPctOf(p);
          const showComparison = plan.apply_discount && disc > 0;
          return (
            <article key={p.id} data-testid={`editor-product-${p.id}`}>
              <div className="six-model-heading">
                <span>0{i + 1}</span>
                <label>Modelo
                  <input type="text" data-testid={`edit-${p.id}-name`} value={p.name} maxLength={70} onChange={(e) => editProduct(p.id, 'name', e.target.value)} />
                </label>
                {resetProduct && (
                  <button
                    type="button"
                    className="product-reset-btn"
                    data-testid={`reset-product-${p.id}`}
                    onClick={() => resetProduct(p.id)}
                    aria-label={`Repor ${p.name} a partir da folha base`}
                    title="Repor este modelo a partir da folha base"
                  >
                    <RotateCcw size={12} />Repor
                  </button>
                )}
              </div>

              {/* Row 1 — preço de tabela (sem desconto), aparece primeiro quando toggle ON */}
              {showComparison && (
                <>
                  <p className="six-model-row-label six-model-row-label--list">
                    Sem desconto · preço de tabela (O2:O7)
                  </p>
                  <div className="six-model-fields four-cols" data-testid={`editor-product-${p.id}-list-row`}>
                    <label>PVP com IVA (€)
                      <input type="number" min="0" max="10000000" step="0.01" data-testid={`edit-${p.id}-list-price`} value={lGross} onChange={(e) => updateListGross(p, e.target.value)} />
                    </label>
                    <label>Preço sem IVA (€)
                      <input type="number" min="0" max="10000000" step="0.01" data-testid={`edit-${p.id}-list-net`} value={lNet} onChange={(e) => updateListNet(p, e.target.value)} />
                    </label>
                    <label>Custo · sem IVA (€)
                      <input type="number" min="0" max="10000000" step="0.01" data-testid={`edit-${p.id}-list-cost`} value={p.cost} onChange={(e) => editProduct(p.id, 'cost', round2(e.target.value))} />
                    </label>
                    <label>Desconto (%)
                      <input type="number" step="0.1" data-testid={`edit-${p.id}-list-discount`} value={0} readOnly disabled title="Preço de tabela — sem desconto aplicado" />
                    </label>
                  </div>
                  <p className="six-model-margin six-model-margin--list" data-testid={`edit-${p.id}-list-margin`}>
                    Margem sem IVA: <strong>{eur(lM, 2)}</strong> · <strong>{num(lPct, 1)}%</strong>
                    <em> · diferença vs. preço c/ desc.: {eur(round2(lM - m), 2)} ({num(round2(lPct - pct), 1)} p.p.)</em>
                  </p>
                  <label className="six-model-margin-slider six-model-margin-slider--list">
                    Margem-alvo (%) · preço de tabela
                    <input type="number" data-testid={`edit-${p.id}-list-margin-pct`} min="-100" max="99.99" step="0.1" value={lPct} onChange={(e) => setListMarginPct(p, e.target.value)} />
                    <small>Recalcula o preço de tabela e ajusta o preço com desconto para manter a proporção actual ({disc}%).</small>
                  </label>
                </>
              )}

              {/* Row 2 — preço actual (com desconto já embutido), aparece a seguir */}
              {showComparison && (
                <p className="six-model-row-label six-model-row-label--discounted">
                  Com desconto ({disc}%) · preço actual
                </p>
              )}
              <div className="six-model-fields four-cols">
                <label>PVP com IVA (€)
                  <input data-testid={`edit-${p.id}-price`} type="number" min="0" max="10000000" step="0.01" value={p.price_gross} onChange={(e) => updateGross(p, e.target.value)} />
                </label>
                <label>Preço sem IVA (€)
                  <input data-testid={`edit-${p.id}-price-net`} type="number" min="0" max="10000000" step="0.01" value={net} onChange={(e) => updateNet(p, e.target.value)} />
                </label>
                <label>Custo · sem IVA (€)
                  <input data-testid={`edit-${p.id}-cost`} type="number" min="0" max="10000000" step="0.01" value={p.cost} onChange={(e) => editProduct(p.id, 'cost', round2(e.target.value))} />
                </label>
                <label>Desconto (%)
                  <input data-testid={`edit-${p.id}-discount`} type="number" min="0" max="95" step="0.1" value={disc} onChange={(e) => updateDiscount(p, e.target.value)} />
                </label>
              </div>
              <p className="six-model-margin" data-testid={`edit-${p.id}-margin`}>
                Margem sem IVA: <strong>{eur(m, 2)}</strong> · <strong>{num(pct, 1)}%</strong>
                {!showComparison && disc > 0 && (
                  <> · <em>preço tabela s/ desc.: {eur(lNet, 2)} → margem {eur(lM, 2)} ({num(lPct, 1)}%)</em></>
                )}
              </p>
              <label className="six-model-margin-slider">
                Margem-alvo (%) · preço com desconto
                <input type="number" data-testid={`edit-${p.id}-margin-pct`} min="-100" max="99.99" step="0.1" value={pct} onChange={(e) => setMarginPct(p, e.target.value)} />
                <small>Recalcula o PVP com desconto para atingir esta margem sobre o preço líquido actual.</small>
              </label>
            </article>
          );
        })}
      </div>
    </>
  );
};

export const MonthlyForecastEditor = ({ plan, editMonthly, editMonthlyBulk, isExample }) => {
  const previous = plan.previous_sales_reference;
  return <>
    <div className="monthly-editor-heading"><span data-testid="monthly-forecast-eyebrow">A BASE É MENSAL. O ANUAL É A SOMA.</span><h3 data-testid="sales-mix-title">Seis modelos. Doze meses.<br/>Uma previsão à sua medida.</h3><p data-testid="monthly-forecast-description">Escreva quantos equipamentos de cada modelo prevê vender em cada mês do primeiro ano. Use os atalhos para ir mais depressa e ajuste depois célula a célula.</p></div>
    {previous&&<details className="previous-sales-reference" data-testid="previous-sales-reference"><summary data-testid="previous-sales-toggle"><History size={15}/>Referência anual anterior · preservada, não aplicada</summary><p data-testid="previous-sales-explanation">{previous.name}. Estes valores não entram nos cálculos atuais.</p><div className="previous-sales-grid">{previous.products.map(p=><div key={p.id} data-testid={`previous-sales-${p.id}`}><span>{p.name}</span><strong>{num(p.units)} un./ano</strong><small>{num(p.share,2)}% no registo anterior</small></div>)}</div><p data-testid="previous-sales-total">Total registado anteriormente: {num(previous.total_units)} unidades/ano.</p></details>}
    <MonthlySalesTable products={plan.products} editMonthly={editMonthly} onBulk={editMonthlyBulk} example={isExample}/>
    <div className="monthly-future-note" data-testid="monthly-future-note">Anos 2–{plan.horizon}: soma do ano 1 × crescimento anual (definido abaixo). Alterar preços, custos ou eventos não mexe nas quantidades que preencheu.</div>
  </>;
};
